/**
 * Studio → Models (Wave 3 PR-A — richer table).
 *
 * Tenant-facing list of Argus brands assigned to this tenant. Read-only.
 *
 * Per founder direction: keep the table format but make it informative
 * and color-coded. We render:
 *   - Brand identity column (icon + display name + brand_id slug)
 *   - Modality pill, color-coded (chat/vision/code/reason/long/image-gen)
 *   - Capability badges row (Vision / Tools / Long ctx / LoRA)
 *   - Context window pill ("32k" / "128k" / "1m") with friendly formatting
 *   - Status dot (warm / cold / unknown) — live, refetched every 30s
 *   - Description (compact, line-clamp-2)
 *   - "Use in chat" action link → /ask-argus?brand=<brandId>
 *
 * Per I-M6.7 every field surfaced here is either tenant-facing public
 * (brandId / displayName / description / modality) or derived from
 * vendor-published model-card facts (supports_*, context_window).
 * Engine model_keys, provenance, signing keys remain redacted.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Cpu, Search, AlertTriangle, Eye, Wrench, BookOpen, Layers,
  CircleDot, ArrowUpRight,
  MessageSquare, Image as ImageIcon, Code2, Brain, FileText, Palette,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useTenantArgusModels,
  type TenantBrand,
  type ModelRuntimeState,
} from '@/lib/use-tenant-argus-models';
import { cn } from '@/lib/utils';
import { matchesModelSearch } from './index.helpers';

export const Route = createFileRoute('/studio/')({
  component: StudioModelsPage,
});

// ─── Modality presentation ──────────────────────────────────────────────────
//
// Color tokens use the Argus design system semantics — semitransparent
// fills on a colored border so the badge reads as a category pill, not
// a status. Dark-mode pairs picked from the existing theme tokens.

const MODALITY_META: Record<
  string,
  { label: string; cls: string; icon: typeof MessageSquare }
> = {
  chat: {
    label: 'Chat',
    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
    icon: MessageSquare,
  },
  vision: {
    label: 'Vision',
    cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30',
    icon: ImageIcon,
  },
  code: {
    label: 'Code',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: Code2,
  },
  reason: {
    label: 'Reasoning',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
    icon: Brain,
  },
  long: {
    label: 'Long context',
    cls: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
    icon: FileText,
  },
  'image-gen': {
    label: 'Image gen',
    cls: 'bg-pink-500/10 text-pink-700 dark:text-pink-300 border-pink-500/30',
    icon: Palette,
  },
};

function getModalityMeta(m: string) {
  return (
    MODALITY_META[m] ?? {
      label: m,
      cls: 'bg-muted text-muted-foreground border-border',
      icon: Layers,
    }
  );
}

// ─── Runtime status dot ────────────────────────────────────────────────────

function RuntimeDot({ state }: { state: ModelRuntimeState }) {
  const meta = {
    warm: { color: 'text-emerald-500 fill-emerald-500', label: 'Warm — loaded and ready' },
    cold: { color: 'text-amber-500 fill-amber-500', label: 'Cold — loads on first request' },
    unknown: { color: 'text-muted-foreground fill-muted-foreground', label: 'Unknown — no probe yet' },
  }[state];
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1.5 cursor-default">
            <CircleDot className={cn('h-2.5 w-2.5', meta.color)} />
            <span className="text-[11px] capitalize text-muted-foreground">{state}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{meta.label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Context window formatter ──────────────────────────────────────────────

function formatContextWindow(k: number | null): string | null {
  if (k === null) return null;
  if (k >= 1000) return `${Math.round(k / 1000)}m`;
  return `${k}k`;
}

// ─── Capability badges ─────────────────────────────────────────────────────

function CapabilityBadges({ caps }: { caps: TenantBrand['capabilities'] }) {
  const items: Array<{ label: string; icon: typeof Eye; on: boolean; tooltip: string }> = [
    { label: 'Vision', icon: Eye, on: caps.vision, tooltip: 'Accepts images alongside text' },
    { label: 'Tools', icon: Wrench, on: caps.tools, tooltip: 'Supports tool / function calling' },
    { label: 'Long ctx', icon: BookOpen, on: caps.longContext, tooltip: '128k+ context window' },
    { label: 'LoRA', icon: Layers, on: caps.lora, tooltip: 'Supports LoRA adapters for fine-tuning' },
  ];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {items.filter((i) => i.on).map((i) => {
        const Icon = i.icon;
        return (
          <TooltipProvider key={i.label} delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="text-[10px] gap-1 font-normal border-border/60 cursor-default"
                >
                  <Icon className="h-2.5 w-2.5" />
                  {i.label}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{i.tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
      {items.every((i) => !i.on) && (
        <span className="text-[11px] text-muted-foreground">—</span>
      )}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

function StudioModelsPage() {
  const { data, isLoading, error } = useTenantArgusModels();
  const [search, setSearch] = useState('');
  const [modalityFilter, setModalityFilter] = useState<string>('all');

  const modalities = useMemo(() => {
    if (!data?.items) return [];
    const set = new Set(data.items.map((b) => b.modality));
    return Array.from(set).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    // #631 — see index.helpers.ts for the underscore/hyphen normalization
    // rationale. The filter contract lives in `matchesModelSearch` so it
    // can be unit-tested without React.
    return data.items.filter((b) => matchesModelSearch(b, search, modalityFilter));
  }, [data, search, modalityFilter]);

  // Codex audit F-S20 / #20: runtime status reads `unknown` for every
  // row when no probe has run. Rather than implying we have stale data
  // we surface an honest banner so users understand the status column
  // is informational. The per-row dot still renders so partial probes
  // (some warm, some unknown) read correctly.
  const allRuntimeUnknown =
    !!data?.items?.length && data.items.every((b) => b.runtimeState === 'unknown');

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Cpu className="h-4 w-4 text-muted-foreground" />
            Available Models
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Argus brands assigned to your workspace. Reference each brand by
            its <code>brandId</code> in the API or pick one in the agent
            builder. Assignments are managed by your platform administrator.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search brands…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        {modalities.length > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setModalityFilter('all')}
              className={cn(
                'px-2 py-1 text-[11px] rounded border transition-colors',
                modalityFilter === 'all'
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
              )}
            >
              All
            </button>
            {modalities.map((m) => {
              const meta = getModalityMeta(m);
              const active = modalityFilter === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModalityFilter(m)}
                  className={cn(
                    'px-2 py-1 text-[11px] rounded border transition-colors capitalize',
                    active ? meta.cls : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
                  )}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        )}
        {data && (
          <span className="text-xs text-muted-foreground tabular-nums ml-auto">
            {filtered.length} {filtered.length === 1 ? 'brand' : 'brands'}
          </span>
        )}
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading models…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load models: {error.message}</div>
          </CardContent>
        </Card>
      ) : !data || data.items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No models have been assigned to your workspace yet. Contact
            your platform administrator to enable Argus brands for this
            tenant.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* 2026-05-12 browser audit P1 #3 — soften the legacy banner.
              The old copy ("Runtime status probing is not yet wired")
              implied the entire build was a stub, which was misleading
              because PH7-4 monitoring landed (Prometheus/Grafana/Loki).
              The /metrics + admin runtime-view + model_load_state path
              IS wired; the banner triggers only when the orchestrator
              hasn't written load-state rows for any of this tenant's
              assigned engines (single-host dev, or an orchestrator
              restart). Direct users to the live runtime view instead
              of telling them probing doesn't exist. */}
          {allRuntimeUnknown && (
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="py-3 px-4 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <strong>Runtime state pending</strong> — the orchestrator has
                  not reported a warm/cold state for any assigned engine yet.
                  Models still load on first request. For live status, open the{' '}
                  <a
                    href="/admin/health"
                    className="underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-100"
                  >
                    Health dashboard
                  </a>
                  .
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-72">Model</TableHead>
                  <TableHead className="w-32">Modality</TableHead>
                  <TableHead className="w-20 text-right">Context</TableHead>
                  <TableHead>Capabilities</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => {
                  const meta = getModalityMeta(b.modality);
                  const Icon = meta.icon;
                  const ctx = formatContextWindow(b.contextWindowK);
                  return (
                    <TableRow key={b.brandId}>
                      <TableCell>
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              'h-8 w-8 rounded-md border flex items-center justify-center shrink-0',
                              meta.cls,
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium leading-tight">
                              {b.displayName}
                              {b.assignedRoleLabel && (
                                <span className="text-[11px] font-normal text-muted-foreground ml-1.5">
                                  · {b.assignedRoleLabel}
                                </span>
                              )}
                            </div>
                            <div className="font-mono text-[11px] text-muted-foreground truncate">
                              {b.brandId}
                            </div>
                            <div className="text-[11px] text-muted-foreground line-clamp-2 max-w-md mt-0.5">
                              {b.description}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-[10px] gap-1 capitalize', meta.cls)}>
                          <Icon className="h-2.5 w-2.5" />
                          {meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {ctx ? (
                          <span className="font-mono text-xs">{ctx}</span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <CapabilityBadges caps={b.capabilities} />
                      </TableCell>
                      <TableCell>
                        <RuntimeDot state={b.runtimeState} />
                      </TableCell>
                      <TableCell className="text-right">
                        {/*
                          Studio is the catalog; Agents is the assembly
                          surface. "Use" should funnel from catalog →
                          assembly, not catalog → chat (Codex audit
                          F-S21 / #21). The agent builder reads
                          `?model=<brandId>` to pre-select this brand.
                        */}
                        <Link
                          to="/agents/builder"
                          search={{ model: b.brandId, editAgentId: undefined }}
                          title={`Start a new agent with ${b.displayName}`}
                          className="inline-flex items-center gap-1 h-7 px-2 text-[11px] rounded-md hover:bg-accent text-foreground"
                        >
                          Use in agent
                          <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
}
